// packages/shared/src/http/api.ts (M0-T02)
import type { FriendColor, Hemisphere } from '@whiskerton/content';
import type { InviteCode, ResidentId, TownId, UserId } from '../types/ids';
import type { Look, Profile } from '../types/profile';
import type { ResidentRole } from '../types/resident';
import type { TownStatus } from '../types/town';

export const API_ERROR_CODES = [
  'unauthorized', 'forbidden', 'not_found', 'invalid', 'name_filtered', 'profile_required',
  'plot_invalid', 'town_full', 'plot_taken', 'already_resident', 'max_residents',
  'invite_expired', 'invite_revoked', 'invite_used_up', 'too_many_towns', 'rate_limited',
  'not_mayor', 'town_archived', 'server_error',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];   // every code has string `errors.<code>` (checked in shared:protocol/codeStrings.test.ts)
export interface ApiError { error: ApiErrorCode; message?: string }

export interface ConfigResponse { apple: boolean; devMode: boolean; protocol: number; build: string; vapidPublicKey: string | null }
export interface TownListEntry {
  id: TownId; name: string; role: ResidentRole; residentId: ResidentId; color: FriendColor;
  mayorName: string; residents: number; joinedAt: number; status: TownStatus;
}
export interface MeResponse { user: { id: UserId; email: string }; profile: Profile | null; towns: TownListEntry[]; isAdmin: boolean }
export interface PutProfileRequest { name: string; look: Look }
export interface PutProfileResponse { profile: Profile }
export interface FoundTownRequest { name: string; hemisphere: Hemisphere; timeZone: string; seed: number; plot: number;
  devClockAt?: string }   // rev 6: DEV_MODE only (400 `invalid` otherwise); forwarded to `init`, which sets the dev clock before any hook runs
export interface FoundTownResponse { town: TownListEntry }
export interface TicketResponse { ticket: string; wsUrl: string; expiresAt: number }   // wsUrl is path-relative: '/api/towns/<id>/ws?ticket=…'
export interface CreateInviteRequest { expiresInHours: number; maxUses: number | null }  // 1 … 720 h; maxUses 1 … 50 or null
export interface InviteView {
  code: InviteCode; url: string; createdAt: number; expiresAt: number;
  maxUses: number | null; uses: number; revoked: boolean; createdByName: string;
}
export interface InviteListResponse { invites: InviteView[] }
export interface InvitePreviewResponse {
  code: InviteCode;
  town: { id: TownId; name: string; mayorName: string; seed: number; genVersion: number; hemisphere: Hemisphere };
  freePlots: number[];
  takenPlots: { plot: number; kind: 'resident' | 'villager'; name: string }[];
  full: boolean;            // no free plot (a villager would move out)
  cannotJoin: boolean;      // full and every villager protected, or 10 humans
  alreadyMember: boolean;
}
export interface AcceptInviteRequest { plot: number | null }    // required when freePlots is non-empty
export interface AcceptInviteResponse { town: TownListEntry; plot: number; displaced: string | null }  // displaced villager name
export interface PushSubscribeRequest { endpoint: string; keys: { p256dh: string; auth: string }; timeZone: string; quietStart: number; quietEnd: number } // minutes of day, device time
export interface PushPrefsRequest { enabled: boolean; timeZone: string; quietStart: number; quietEnd: number }
export interface BlockListResponse { blocks: { userId: UserId; name: string; at: number }[] }
export type ReportKind = 'chat' | 'letter' | 'name' | 'town_name';
export interface ReportRequest { townId: TownId; kind: ReportKind; refId: string; note: string | null } // refId: chat id | mail id | resident id | town id
export interface AdminReport {
  id: string; kind: ReportKind; townId: TownId; townName: string; targetUserId: UserId | null; targetName: string;
  content: string; reporterUserId: UserId | null; reporterName: string | null; note: string | null; createdAt: number;
  status: 'open' | 'resolved'; resolution: string | null;
}
export interface ClientErrorReport { message: string; stack: string | null; build: string; url: string; at: number }
export interface DevOutboxResponse { messages: { to: string; subject: string; otp: string | null; at: number }[] }
export interface DevClockRequest { offsetMs?: number; at?: string }   // `at`: town-local 'YYYY-MM-DDTHH:mm'
